export enum TransactionStatusEnum {
  Waiting = 0, // New - waiting for signatures
  Executed = 1, // All signatures collected, executing
  Deleted = 2, // Transaction was deleted
  Expired = 3, // Transaction expired
  Failed = 4, // Transaction failed
  Success = 5, // Successfully executed on network
  AwaitingExecution = 6, // All signatures collected, waiting for expiry time
}

export enum TransactionListLabels {
  ACTION = 'Action',
  AMOUNT = 'Amount / Method',
  TO_TARGET = 'To / Target',
  EXPIRES = 'Expires',
  THRESHOLD = 'Threshold',
  STATUS = 'Status',
}

export interface TransferParams {
  amount: number;
  description: string;
  toAccountId: string;
}

export interface ContractInteractionParams {
  contractId: string;
  contractMethod: string;
  params: Array<{
    type: string;
    value: any;
  }>;
  gas?: number;
  payableAmount?: number;
}

export interface TokenTransferParams {
  tokenId: string;
  tokenSymbol: string;
  tokenDecimals: number;
  amount: number;
  toAccountId: string;
}

export interface TokenAssociationParams {
  tokenId: string;
  tokenName?: string;
  tokenSymbol?: string;
}

export interface FundParams {
  amount: number;
  transactionId?: string;
}

export interface ThresholdUpdateParams {
  oldThreshold?: number;
  oldSigners?: { accountId: string; name: string }[];
  newThreshold?: number;
  newSigners?: { accountId: string; name: string; publicKey?: string }[];
}

export type TransactionParams =
  | TransferParams
  | ContractInteractionParams
  | TokenTransferParams
  | TokenAssociationParams
  | FundParams
  | ThresholdUpdateParams;

export interface TransactionType {
  _id: string;
  type: string;
  status: number;
  signed: boolean;
  createdAt: string;
  expires_at: number;
  executed_at?: number;
  wait_for_expiry?: boolean;
  description?: string;
  schedule_id: string;
  tx_id?: string;
  wallet_account_id?: string;
  network: string;
  required_signatures: number;
  total_signers: number;
  signatures_count: number;
  signatures: Array<{
    transactionId: string;
    key: string;
    accountId: string;
  }>;
  params: TransactionParams;
  signed_tx_bytes?: string; // Base64 encoded signed transaction bytes for manual multi-sig
}

// Type guards for transaction params
export function isTransferParams(params: TransactionParams): params is TransferParams {
  return 'toAccountId' in params && 'amount' in params;
}

export function isContractInteractionParams(
  params: TransactionParams,
): params is ContractInteractionParams {
  return 'contractId' in params && 'contractMethod' in params;
}

export function isTokenTransferParams(params: TransactionParams): params is TokenTransferParams {
  return 'tokenId' in params && 'toAccountId' in params && 'amount' in params;
}

export function isTokenAssociationParams(
  params: TransactionParams,
): params is TokenAssociationParams {
  return 'tokenId' in params && !('amount' in params) && !('contractId' in params);
}

export function isThresholdUpdateParams(
  params: TransactionParams,
  type?: string,
): params is ThresholdUpdateParams {
  if (type) return type === 'ThresholdUpdate';
  return 'newSigners' in params;
}

export function isFundParams(params: TransactionParams, type?: string): params is FundParams {
  if (type) return type === 'Fund';
  return 'amount' in params && !('toAccountId' in params) && !('tokenId' in params);
}

// Shared base for unified transaction list (incoming + outgoing)
export interface BaseTransaction {
  _id: string;
  direction: 'incoming' | 'outgoing';
  createdAt: string;
  network: string;
}

// Outgoing transactions — all fields from TransactionType plus direction discriminant
export interface OutgoingTransaction extends BaseTransaction {
  direction: 'outgoing';
  type: string;
  status: number;
  signed: boolean;
  expires_at: number;
  executed_at?: number;
  wait_for_expiry?: boolean;
  description?: string;
  schedule_id: string;
  tx_id?: string;
  wallet_account_id?: string;
  required_signatures: number;
  total_signers: number;
  signatures_count: number;
  signatures: Array<{
    transactionId: string;
    key: string;
    accountId: string;
  }>;
  params: TransactionParams;
  signed_tx_bytes?: string;
}

// Incoming transactions — received HBAR, tokens, NFTs, airdrops
export interface IncomingTransactionType extends BaseTransaction {
  direction: 'incoming';
  transferType: 'hbar' | 'token' | 'nft' | 'airdrop';
  tx_id: string;
  consensusTimestamp?: string;
  senderAccountId: string;
  amount?: number;
  tokenId?: string;
  tokenSymbol?: string;
  tokenName?: string;
  tokenDecimals?: number;
  nftSerialNumber?: number;
  type?: string;
  description?: string;
}

// Discriminated union for the unified transaction list
export type UnifiedTransaction = OutgoingTransaction | IncomingTransactionType;

// Type guard for incoming transactions
export function isIncomingTransaction(tx: UnifiedTransaction): tx is IncomingTransactionType {
  return tx.direction === 'incoming';
}

export interface CreateTransaction {
  contractId: string;
  contractMethod: string;
  params: Array<{
    type: string;
    value: any;
  }>;
  scheduleId?: string;
  txId?: string;
  gas?: number;
  payableAmount?: number;
  description?: string;
  waitForExpiry?: boolean;
  expiresAt?: number;
}

export interface TxInfo {
  from: string;
  to: string;
  amount: number;
}

export interface SignerDto {
  accountId: string;
  publicKey: string;
  name?: string;
}

export interface UpdateThresholdData {
  scheduleId: string;
  transactionId: string;
  newThreshold?: number;
  newSigners?: SignerDto[];
  description?: string;
}

export interface UpdateMetadataData {
  name?: string;
  keys?: {
    accountId: string;
    name: string;
  }[];
}
